import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState, ViewStateRecordsAlias } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { DiscoverSearchResults } from '@discover/data-models/discover-search-results/discover-search-results';
import { ApWireRecord } from '@discover/data-models/records/ap-wire-record';
import { Store } from '@ngrx/store';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { ServiceCollectionRecord } from '@src/suite/common/data-models/service/service-collection-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { Convertor } from '@src/suite/common/services/convertor';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { NavigationItem } from '@src/suite/common/store/navigation/models/navigation.models';
import { selectCurrentItem } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { Util } from '@src/suite/common/utils/util';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { AppService } from '@suite/app.service';
import { DataStore } from '@suite/common/data-models/data-store/data-store';
import { BasicContentService } from '@suite/common/services/basic-content-service';
import { PlaybookService } from '@suite/playbook/services/playbook-service';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';

import { DiscoverSearchService } from './discover-search-service';

@Injectable({ providedIn: 'root' })
export class DiscoverService /*implements IDiscoverService*/ {
    private viewStateRecordSubscription: Subscription;
    private viewStateRecordPropertySubscription: Subscription;

    public WireRequiredFieldDefs: Array<FieldDef> = [];
    public WireRequiredFieldDefsDict: { [id: string]: FieldDef } = {};

    public groupByWires: Array<{ id: string; title: string }> = [];
    public sortByWires: Array<{ id: string; title: string }> = [];

    destroyed$: Subject<void> = new Subject<void>();
    public _item: NavigationItem;
    item$ = this.store
        .select(selectCurrentItem)
        .pipe(
            tap(item => {
                this._item = item ? item : null;
            }),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    constructor(
        public appService: AppService,
        public discoverSearchService: DiscoverSearchService,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public ngZone: NgZone,
        public navigationService: NavigationService,
        public convertor: Convertor,
        private router: Router,
        public playbookService: PlaybookService,
        public basicContentService: BasicContentService,
        public playbookSearchService: PlaybookSearchService,
        private store: Store
    ) {
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.MULTIPLEFEED] = this.getFiltersForMultiWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.RSSFEED] = this.getFiltersForSingleWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.TWITTERFEED] = this.getFiltersForSingleWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.WIREFEED] = this.getFiltersForSingleWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.APFEED] = this.getFiltersForSingleWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.FTPFEED] = this.getFiltersForSingleWireFeed.bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.WIRESEARCH] = this.getFiltersForWireSearch.bind(this);

        this.globalTablesService.clearServices.discover = this.clearDiscoverService.bind(this);
    }

    public getFiltersForSingleWireFeed(item: FieldDef): boolean {
        if (item.Briefable && item.DiscoverWireProp === true) {
            return true;
        }
        return false;
    }

    public getFiltersForWireSearch(item: FieldDef): boolean {
        if (item.Briefable && item.DiscoverWireProp === true) {
            return true;
        }
        return false;
    }

    public getFiltersForMultiWireFeed(item: FieldDef): boolean {
        if (
            item.Briefable &&
            item.DiscoverWireProp === true &&
            (item.ID === PSTFieldNames.PSTWirePublished || item.ID === PSTFieldNames.PSTWireProvider || item.ID === PSTFieldNames.PSTWireCategory)
        ) {
            return true;
        }
        return false;
    }

    public get discoverSearchResults(): DiscoverSearchResults {
        return DataStore.getSearchResults<DiscoverSearchResults>('discover');
    }
    public activeCollectionPollerStarted: boolean = false;

    public async createDiscoverItem(wire: StorytellingWireRecord): Promise<ServiceResponse<StorytellingRecord>> {
        let response: HttpResponse<unknown> = null;
        try {
            const fieldsToUpdate: { [id: string]: any } = {
                [PSTFieldNames.Type]: ObjectTypes.DISCOVER_ITEM,
                [PSTFieldNames.Title]: wire.Title,
                [PSTFieldNames.Creator]: this.nomWebServicesJson.UserId,
                [PSTFieldNames.PSTCreatedBy]: this.nomWebServicesJson.UserId,
                Text: wire.Content,
            };
            Util.escapeSpecialCharsForFileName(fieldsToUpdate);

            const requestParams = {
                database: this.nomWebServicesJson.PSTServer,
                path: `P_${this.nomWebServicesJson.PSTGroup}\\W\\${this.nomWebServicesJson.DiscoverHoldFolder}`,
                dateTime: '',
                user: this.nomWebServicesJson.UserId,
                fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fieldsToUpdate),
                children: [],
                stories: null,
                templateGuid: null,
                templateProgramId: null,
                linkToParent: false,
                wireDatabase: null,
                wirePath: null,
                wireGuid: null,
                title: wire.Title,
            };

            response = await this.nomWebServicesJson.put('/api/DiscoverItem/CreateDiscoverItem', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create discover item',
                detailMessage: `Unable to create discover item. \n\rStackTrace: DiscoverService.createDiscoverItem(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingRecord>(false, `Unable to create discover item ${error.toString()}`);
        }

        const serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();

        const discoverItemRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingRecord>(serviceRecord);

        return new ServiceResponse<StorytellingRecord>(true, discoverItemRecord);
    }

    public async attachDiscoverItem(wire: StorytellingWireRecord, topic: StorytellingTopicRecord): Promise<ServiceResponse<StorytellingTopicRecord>> {
        let response: HttpResponse<unknown> = null;
        try {
            const fieldsToUpdate: { [id: string]: any } = {
                [PSTFieldNames.Type]: ObjectTypes.DISCOVER_ITEM,
                [PSTFieldNames.Title]: wire.Title,
                [PSTFieldNames.Creator]: this.nomWebServicesJson.UserId,
                [PSTFieldNames.PSTCreatedBy]: this.nomWebServicesJson.UserId,
                Text: wire.Content,
            };
            Util.escapeSpecialCharsForFileName(fieldsToUpdate);

            const requestParams = {
                database: this.nomWebServicesJson.PSTServer,
                path: `P_${this.nomWebServicesJson.PSTGroup}\\W\\${this.nomWebServicesJson.DiscoverHoldFolder}`,
                dateTime: '',
                user: this.nomWebServicesJson.UserId,
                fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fieldsToUpdate),
                children: [],
                stories: null,
                templateGuid: null,
                templateProgramId: null,
                linkToParent: false,
                wireDatabase: null,
                wirePath: null,
                wireGuid: null,
                title: wire.Title,
                parentGuid: topic.Guid,
            };

            response = await this.nomWebServicesJson.put('/api/DiscoverItem/AttachDiscoverItemToTopic', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to attach to topic',
                detailMessage: `Unable to attach to topic. \n\rStackTrace: DiscoverService.createDiscoverItem(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingTopicRecord>(false, `Unable to attach to topic ${error.toString()}`);
        }

        const serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();

        const topicRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceRecord);
        return new ServiceResponse<StorytellingTopicRecord>(true, topicRecord);
        // await DataStore.setCollection(topicRecord, true);
        // const record = DataStore.byGuid<StorytellingTopicRecord>(topicRecord.guid, '');
        // record.links = [];
        // return new ServiceResponse<TopicRecord>(true, DataStore.byGuid<StorytellingTopicRecord>(topicRecord.guid, ''));
    }

    public async loadApWireById(wireId: string): Promise<ApWireRecord> {
        const res: ServiceResponse<ApWireRecord> = await this.discoverSearchService.getApWireById(wireId);
        return res.data;
    }

    public invalidateVisualState() {
        setTimeout(async () => {
            if (!this._item || !this.navigationService.isNewView(this._item)) {
                this.navigationService.currentListView.hideLoader();
            }

            if (this._item && this.navigationService.isNewView(this._item)) {
                this.store.dispatch(NavigationApiActions.saveCurrentNavigationItem());
                this.navigationService.currentListView.hideLoaderDirectly();
            }
        }, 0);
    }

    public populateDefaultValuesForWire(_f: FieldDef, _criteriaObj: any) {}

    public async getSavedViewItems(term: string): Promise<any> {
        const vs: SearchViewStateRecord = new SearchViewStateRecord();
        vs.viewStateType = ViewStateTypes.SEARCH;
        vs.viewStateSearchQuery = term;
        const res = await this.playbookSearchService.getPlaybookResults(<ViewStateRecordsAlias>vs);
        if (res.status) {
            return res.data.topics;
        }
    }

    public clearDiscoverService() {
        this.groupByWires = [];
        this.sortByWires = [];
    }
}
